import * as React from 'react'
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

import { OVERLAY_POPUP_Z_CLASS } from './overlay-panel'

/* -------------------------------------------------------------------------
 * shadcn `DropdownMenu` primitive, brought in with
 * `npx shadcn@latest add dropdown-menu`. It backs the `src/antd/Dropdown`
 * seam's shadcn half and nothing else; nothing reaches it until someone flips
 * the design toggle.
 *
 * Three edits to the CLI's output:
 *
 * 1. `container` ON `DropdownMenuContent`, for the same reason `./popover`
 *    grew one: the seam keeps antd's own `getPopupContainer` default — here
 *    the trigger's parent — rather than re-homing 29 call sites to
 *    `document.body`.
 * 2. THE STACKING comes from `./overlay-panel` — antd puts a Dropdown menu at
 *    `zIndexPopupBase + 50`, the same level as a Select popup.
 * 3. THE CHECKBOX / RADIO / SUBMENU PARTS ARE DROPPED. No `menu.items` in this
 *    repo carries `children` on a non-group item (a submenu) and nothing asks
 *    for a checkable row, so those exports would be dead code — and each of
 *    them is what pulls `lucide-react` into the CLI's output, which outside
 *    `src/icons/antd-compat` renders lucide with the design toggle OFF
 *    (AGENTS.md; `src/antd/__tests__/seam-adoption.evidence.test.tsx` fails on
 *    it). Removing them removed the import rather than re-pointing it.
 *
 * One appearance edit on top of those: an item is `cursor-pointer`, not the
 * CLI's `cursor-default`. antd's `.ant-dropdown-menu-item` has always shown a
 * pointer, and every one of this repo's menus is a list of actions; a row that
 * does not look clickable is a behaviour report waiting to happen.
 *
 * `'use client'` is dropped too: Vite SPA, and no other file in this directory
 * carries it.
 * ------------------------------------------------------------------------- */

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

// `forwardRef`, not a bare function: the Dropdown seam takes a ref to the
// trigger and reads its parent element to answer `getPopupContainer`. A
// wrapper that swallows the ref silently sends every menu to `document.body`.
const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(function DropdownMenuTrigger(props, ref) {
  return (
    <DropdownMenuPrimitive.Trigger
      ref={ref}
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  )
})

function DropdownMenuContent({
  className,
  sideOffset = 4,
  container,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  /** Portal target. `undefined` = `document.body`, Radix's own default. */
  container?: HTMLElement | null
  /**
   * Radix forwards this to the menu's `FocusScope` at runtime, but omits it
   * from `DropdownMenuContentProps` as a private prop (`MenuContentImpl`
   * declares it in `MenuContentImplPrivateProps`). The Dropdown seam needs it:
   * a `dropdownRender` panel owns its own focus, and a menu driven from an
   * input OUTSIDE it must not pull the caret away. Re-declared here rather
   * than cast at the call site, so a Radix release that drops the forwarding
   * is caught by the one file that knows about it.
   */
  onOpenAutoFocus?: (event: Event) => void
}) {
  return (
    <DropdownMenuPrimitive.Portal container={container ?? undefined}>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md',
          OVERLAY_POPUP_Z_CLASS,
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  )
}

function DropdownMenuItem({
  className,
  inset,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  variant?: 'default' | 'destructive'
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:*:[svg]:text-destructive! [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        'px-2 py-1.5 text-sm font-medium data-[inset]:pl-8',
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
}
